<template>
  <FriedModalSubContainer>
    <template #header>
      <FriedModalHeader :header="$t('sign-document')" />
    </template>
    <div class="description">
      {{ signText }}
      <span v-if="!!entity.isEsignConsent" class="sign-consent">
        {{ $t('you-affirmatively-consent-to-the-terms-of-the') }}
        <a class="consent-link" @click="handleShowConsent">{{ $t('e-signing-disclosure') }}</a>
      </span>
    </div>
    <FriedInput
      v-if="!hasInitials && isSigningInitials"
      v-model="initials"
      v-autofocus
      class="initials-input"
      :error-message="initialsValidation"
      data-external="verify-sign-intitials-input"
      :label="$t('initials')"
      @keydown.enter="handleSignDocument"
    />
    <template #footer>
      <FriedModalButtonsFooter>
        <FriedButton
          data-external="verify-click-cancel-button"
          :button-type="ButtonType.Secondary"
          @click="close"
        >
          {{ $t('cancel') }}
        </FriedButton>
        <FriedButton
          class="sign-button"
          data-external="verify-click-sign-button"
          @click="handleSignDocument"
        >
          {{ $t('sign-document') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
    <SignConsent v-if="showConsent" class="consent-container" :entity="entity" :sender="sender" />
  </FriedModalSubContainer>
</template>

<script lang="ts">
import { ButtonType } from '@getaccept/fried-tofu';
import { SignatureConstants } from '@getaccept/lib-shared-new/src/signature/constants/signature.constants';
import type { Entity } from '@getaccept/lib-shared-new/src/entity/signing-site/types/entity';
import type { SigningPageUser } from '@getaccept/lib-shared-new/src/types/signing-page-user';
import type { SigningPageDocument } from '@getaccept/lib-shared-new/src/types/signing-page-document';
import type { PropType } from 'vue';
import { watch, computed, ref, defineComponent } from 'vue';
import { SigningHelper } from '../../helpers/signing.helper';
import SignConsent from './ESignConsent.vue';

export default defineComponent({
  components: {
    SignConsent,
  },
  props: {
    entity: {
      type: Object as PropType<Entity>,
      required: true,
    },
    sender: {
      type: Object as PropType<SigningPageUser>,
      required: true,
    },
    document: {
      type: Object as PropType<SigningPageDocument>,
      required: true,
    },
    signText: {
      type: String,
      default: '',
    },
    reviewPending: {
      type: Boolean,
      default: false,
    },
    hasInitials: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const initials = ref('');
    const initialsValidation = ref('');
    const showConsent = ref(false);

    const isSigningInitials = computed((): boolean => props.document.isSigningInitials === 1);

    const watchInitials = () => {
      initialsValidation.value = '';
    };
    watch(initials, watchInitials);

    const handleShowConsent = (): void => {
      showConsent.value = true;
    };

    const handleSignDocument = () => {
      if (props.reviewPending) {
        close();
        return;
      }

      if (!isSigningInitials.value || props.hasInitials) {
        emit('sign', '');
        return;
      }

      initialsValidation.value = SigningHelper.validateInitials(initials.value);
      if (!initialsValidation.value) {
        emit('sign', initials.value);
      }
    };

    const close = () => {
      emit('close');
    };

    return {
      ButtonType,
      initialsMaxLength: SignatureConstants.initialsMaxLength,
      initials,
      initialsValidation,
      showConsent,
      isSigningInitials,
      handleShowConsent,
      handleSignDocument,
      close,
    };
  },
});
</script>
<style lang="scss" scoped>
.consent-link {
  display: inline;
  padding: 0;
  color: var(--info-dark);
  cursor: pointer;
}

.consent-container {
  margin-top: var(--spacing-100);
}

.initials-input {
  width: 100%;
}
</style>
